import { DbFormType, HandlersActionResponse } from '../app/athenaApiGen';

const isFormBasedType = (abbreviation: string) => {
  const lowerAbb = abbreviation.toLocaleLowerCase();
  return lowerAbb.includes('ctp') ||
    lowerAbb.includes('tr') ||
    lowerAbb.includes('rip') ||
    lowerAbb.includes('tar') ||
    lowerAbb.includes('pnr')
    ? true
    : false;
};

const isActionEnabled = (
  allowedActions: HandlersActionResponse[],
  actionString: string,
) => allowedActions.find((a) => a.action === actionString);

const getDeliverableType = (abbreviation: string) =>
  isFormBasedType(abbreviation) ? abbreviation?.toLowerCase() : 'deliverable';

const getDeliverableState = (workflowState: string) =>
  workflowState === 'Pending'
    ? 'inflight'
    : workflowState?.includes('Review')
      ? 'submitted'
      : 'submitted'; //for now just using submitted, because there is no completed for viewing actions.

// Maps short abbreviations to the exact formType strings returned by GET /api/forms
const formTypeNameMap: Record<string, string> = {
  ctp: 'Consent To Purchase (CTP) Form',
  pnr: 'Problem Notification Report (PNR) Form',
  rip: 'Request to Initiate Purchase (RIP) Form',
  tar: 'Trip Authorization Request (TAR) Form',
  tr: 'Trip Report (TR) Form',
};

const isFormTypeAvailable = (
  availableForms: DbFormType[],
  formType: string,
) => {
  const expectedName = formTypeNameMap[formType.toLowerCase()];
  if (!expectedName) return false;
  return availableForms.some(
    (f) => f.formType?.toLowerCase() === expectedName.toLowerCase(),
  );
};

const getEnabledMenuItems = (
  actions: HandlersActionResponse[],
  availableForms: DbFormType[] = [],
) => ({
  approvalQueue: isActionEnabled(actions, 'view-approval-queue'),
  home: isActionEnabled(actions, 'view-homepage'),
  financials: isActionEnabled(actions, 'view-financial-views'),
  library: isActionEnabled(actions, 'view-library'),
  ims: isActionEnabled(actions, 'view-ims-summary'),
  projects: isActionEnabled(actions, 'list-projects'),
  deliverables:
    isActionEnabled(actions, 'list-inflight-deliverables') ||
    isActionEnabled(actions, 'list-submitted-deliverables'),
  // Admin permission gates surviving the services-0.11.0 ACL revert (commit
  // 0daae3bb). The 6 invented `admin-platform-*` / `admin-manage-*` actions
  // proposed for SPRINT-001 were dropped — admin CRUD endpoints all gate on
  // `admin-sitewide-access` instead. The remaining 5 keys below are real seed
  // rows that survived: `admin-set-workflow-types` still gates ProgramWorkflowType.
  adminFinancialPlans: isActionEnabled(actions, 'admin-financial-plans'),
  adminIms: isActionEnabled(actions, 'admin-ims'),
  adminSidewideAccess: isActionEnabled(actions, 'admin-sitewide-access'),
  adminSetDeliverables: isActionEnabled(actions, 'admin-set-deliverables'),
  adminSetWorkflowTypes: isActionEnabled(actions, 'admin-set-workflow-types'),
  forms: {
    ctp:
      isActionEnabled(actions, 'create-ctp') &&
      isFormTypeAvailable(availableForms, 'ctp'),
    tr:
      isActionEnabled(actions, 'create-tr') &&
      isFormTypeAvailable(availableForms, 'tr'),
    rip:
      isActionEnabled(actions, 'create-rip') &&
      isFormTypeAvailable(availableForms, 'rip'),
    tar:
      isActionEnabled(actions, 'create-tar') &&
      isFormTypeAvailable(availableForms, 'tar'),
    pnr:
      isActionEnabled(actions, 'create-pnr') &&
      isFormTypeAvailable(availableForms, 'pnr'),
  },
});

// Returns true if the user has ANY admin-related permission. Used to decide
// whether to show the "Admin" entry in the header user menu. `adminSidewideAccess`
// acts as a superuser override — a platform admin with site-wide access sees the
// full admin UI even if they don't have the more granular surviving keys.
type EnabledItems = ReturnType<typeof getEnabledMenuItems>;

const hasAnyAdminPermission = (enabled: EnabledItems): boolean =>
  !!(
    enabled.adminSidewideAccess ||
    enabled.adminFinancialPlans ||
    enabled.adminIms ||
    enabled.adminSetDeliverables ||
    enabled.adminSetWorkflowTypes
  );

// Admin-scope gate resolver with sitewide-access superuser fallback.
// Returns true if the user has adminSidewideAccess OR the specific required key.
// Used by the Admin landing to decide which tabs, sections and items to render.
const isAdminGateOpen = (
  enabled: EnabledItems,
  key: keyof EnabledItems,
): boolean => {
  if (enabled.adminSidewideAccess) return true;
  return !!enabled[key];
};

export {
  getDeliverableState,
  getDeliverableType,
  getEnabledMenuItems,
  hasAnyAdminPermission,
  isActionEnabled,
  isAdminGateOpen,
  isFormTypeAvailable,
};
